import { useState, useEffect } from "react";
import { useApp } from "../context/AppContext";
import { SettingsModal } from "./SettingsModal";
import { AIDifficulty } from "../types/pokemon";
import { translateType } from "../utils/typeUtils";

const DIFFICULTIES: { value: AIDifficulty; label: string; desc: string; emoji: string }[] = [
  { value: "easy", label: "Facile", desc: "IA aleatoire", emoji: "😊" },
  { value: "normal", label: "Normal", desc: "IA stats", emoji: "🤔" },
  { value: "hard", label: "Difficile", desc: "IA synergie", emoji: "😈" },
];

const SHOWCASE_IDS = [
  1, 4, 7, 25, 39, 94, 130, 131, 143, 149, 150, 151,
  152, 155, 158, 196, 248, 249, 250, 251,
  252, 255, 258, 282, 373, 376, 384, 386,
  387, 390, 393, 445, 448, 475, 493,
  495, 498, 501, 571, 609, 635, 637, 646,
  650, 653, 656, 700, 706, 718,
  722, 725, 728, 785, 800, 809,
  810, 813, 816, 858, 887, 898,
  906, 909, 912, 1000, 1007, 1024,
];

interface SpriteData {
  id: number;
  delay: number;
  x: number;
  y: number;
  size: number;
  duration: number;
}

function FloatingSprite({ id, delay, x, y, size, duration }: SpriteData) {
  return (
    <img
      src={`https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${id}.png`}
      alt=""
      className="absolute pointer-events-none select-none"
      style={{
        width: size, height: size, left: `${x}%`, top: `${y}%`,
        opacity: 0.1, imageRendering: "pixelated",
        animation: `homeFloat ${duration}s ease-in-out infinite`,
        animationDelay: `${delay}s`,
        filter: "brightness(1.5) saturate(0.3)",
      }}
    />
  );
}

export function HomeScreen() {
  const { state, dispatch } = useApp();
  const [showSettings, setShowSettings] = useState(false);
  const [showDifficulty, setShowDifficulty] = useState(false);
  const [floatingSprites, setFloatingSprites] = useState<SpriteData[]>([]);

  useEffect(() => {
    const cols = 5;
    const rows = 4;
    const count = cols * rows;
    const shuffled = [...SHOWCASE_IDS].sort(() => Math.random() - 0.5);
    const sprites: SpriteData[] = [];
    for (let i = 0; i < count; i++) {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const cellW = 100 / cols;
      const cellH = 100 / rows;
      const jitterX = (Math.random() - 0.5) * cellW * 0.6;
      const jitterY = (Math.random() - 0.5) * cellH * 0.5;
      sprites.push({
        id: shuffled[i % shuffled.length],
        delay: Math.random() * 6,
        x: col * cellW + cellW / 2 - 3 + jitterX,
        y: row * cellH + cellH / 2 - 3 + jitterY,
        size: 40 + Math.random() * 36,
        duration: 7 + Math.random() * 7,
      });
    }
    setFloatingSprites(sprites);
  }, []);

  const totalSaved = state.savedTeams.length;
  const { settings } = state;

  const handleStartDraft = () => {
    dispatch({ type: "SET_MODE", mode: "2p" });
    dispatch({ type: "SET_SCREEN", screen: "draft" });
  };

  const handleSoloStart = (difficulty: AIDifficulty) => {
    dispatch({ type: "SET_MODE", mode: "solo", difficulty });
    dispatch({ type: "SET_SCREEN", screen: "draft" });
  };

  return (
    <div className="relative w-full h-full overflow-hidden">
      {/* Background layers */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {floatingSprites.map((sprite, i) => (
          <FloatingSprite key={i} {...sprite} />
        ))}
      </div>
      <div className="absolute inset-0 pokeball-pattern opacity-15 pointer-events-none" />
      {/* Vignette */}
      <div className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.4) 100%)" }} />

      {/* Main layout */}
      <div className="relative z-10 h-full flex flex-col items-center justify-center p-6">

        {/* ====== ROW 1: Big title ====== */}
        <div className="text-center mb-12">
          <div className="relative inline-block mb-4">
            <img
              src="/pokeball-large.png"
              alt="Pokeball"
              className="w-20 h-20 mx-auto"
              style={{
                imageRendering: "pixelated",
                animation: "spin-pokeball 4s ease-in-out infinite",
                filter: "drop-shadow(0 0 20px rgba(255,50,50,0.5)) drop-shadow(0 0 60px rgba(255,50,50,0.15))",
              }}
            />
            <div className="absolute inset-0 rounded-full"
              style={{ animation: "pulse-glow 3s ease-in-out infinite", opacity: 0.2,
                background: "radial-gradient(circle, rgba(255,100,100,0.3) 0%, transparent 70%)" }} />
          </div>
          <h1 className="font-pokemon text-5xl md:text-6xl text-poke-yellow mb-3"
            style={{ textShadow: "0 0 30px rgba(255,222,0,0.5), 0 0 60px rgba(255,222,0,0.2), 4px 4px 0 rgba(0,0,0,0.5)" }}>
            POKEMON
          </h1>
          <div className="font-pokemon text-base md:text-lg text-white/60 tracking-[0.3em]"
            style={{ textShadow: "0 2px 4px rgba(0,0,0,0.5)" }}>
            STARTER GENERATOR
          </div>
          <div className="w-72 h-[2px] mx-auto mt-4 bg-gradient-to-r from-transparent via-poke-yellow/40 to-transparent" />
        </div>

        {/* ====== ROW 2: Menu (left) + Config (right) side by side ====== */}
        <div className="flex flex-row items-start justify-center gap-8">

          {/* Left: Menu buttons */}
          <div className="flex flex-col gap-3 w-80">

            {/* 2 Players */}
            <MenuButton
              onClick={handleStartDraft}
              gradient="linear-gradient(135deg, #3B82C8 0%, #1A4675 100%)"
              glowColor="rgba(59,130,200,0.4)"
              icon="⚔️"
              label="2 Joueurs"
              sublabel="Draft local"
            />

            {/* Solo */}
            <MenuButton
              onClick={() => setShowDifficulty(!showDifficulty)}
              gradient="linear-gradient(135deg, #22C55E 0%, #166534 100%)"
              glowColor="rgba(34,197,94,0.3)"
              icon="🎮"
              label="Solo"
              sublabel="Contre l'IA"
            />

            {showDifficulty && (
              <div className="flex gap-2 animate-fade-in ml-4">
                {DIFFICULTIES.map((d) => (
                  <button
                    key={d.value}
                    onClick={() => handleSoloStart(d.value)}
                    className="flex-1 font-pokemon text-[9px] py-3 px-2 rounded-xl text-white transition-all hover:scale-105 active:scale-95 flex flex-col items-center gap-1"
                    style={{
                      background: "linear-gradient(135deg, #4ADE80 0%, #16A34A 100%)",
                      border: "2px solid rgba(255,255,255,0.12)",
                      boxShadow: "0 2px 12px rgba(34,197,94,0.25)",
                    }}
                  >
                    <span className="text-lg">{d.emoji}</span>
                    <span>{d.label}</span>
                    <span className="text-[7px] text-white/50 normal-case">{d.desc}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Collection */}
            <MenuButton
              onClick={() => dispatch({ type: "SET_SCREEN", screen: "collection" })}
              gradient="linear-gradient(135deg, #A855F7 0%, #6B21A8 100%)"
              glowColor="rgba(168,85,247,0.3)"
              icon="📋"
              label="Mes equipes"
              sublabel={`${totalSaved} sauvegardee${totalSaved !== 1 ? "s" : ""}`}
            />
          </div>

          {/* Right: settings panel */}
        <div
          className="w-72 rounded-2xl overflow-hidden shrink-0 self-center"
          style={{
            background: "linear-gradient(160deg, rgba(15,25,50,0.85) 0%, rgba(10,15,35,0.9) 100%)",
            border: "2px solid rgba(255,255,255,0.08)",
            boxShadow: "0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.05)",
            backdropFilter: "blur(16px)",
          }}
        >
          {/* Panel header */}
          <div className="px-5 py-3 flex items-center justify-between"
            style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
            <div className="flex items-center gap-2">
              <span className="text-sm">⚙</span>
              <span className="font-pokemon text-[10px] text-white/70 tracking-wider">CONFIG</span>
            </div>
            <button
              onClick={() => setShowSettings(true)}
              className="font-pokemon text-[8px] text-poke-yellow/60 hover:text-poke-yellow transition-colors"
            >
              MODIFIER ▸
            </button>
          </div>

          {/* Quick settings preview */}
          <div className="px-5 py-4 flex flex-col gap-3">

            {/* Generations */}
            <SettingRow label="Generations">
              <div className="flex gap-1 flex-wrap">
                {[1,2,3,4,5,6,7,8,9].map(g => (
                  <span key={g}
                    className="font-retro text-[8px] w-5 h-5 rounded flex items-center justify-center"
                    style={{
                      background: settings.enabledGenerations.includes(g) ? "rgba(255,222,0,0.2)" : "rgba(255,255,255,0.03)",
                      color: settings.enabledGenerations.includes(g) ? "#FFDE00" : "rgba(255,255,255,0.15)",
                      border: `1px solid ${settings.enabledGenerations.includes(g) ? "rgba(255,222,0,0.3)" : "rgba(255,255,255,0.05)"}`,
                    }}>
                    {g}
                  </span>
                ))}
              </div>
            </SettingRow>

            {/* Cards + Rerolls */}
            <div className="flex gap-4">
              <SettingRow label="Cartes">
                <span className="font-retro text-sm text-poke-yellow">{settings.cardsPerDraw}</span>
              </SettingRow>
              <SettingRow label="Rerolls">
                <span className="font-retro text-sm text-poke-yellow">{settings.maxRerolls}</span>
              </SettingRow>
              <SettingRow label="Tier">
                <span className="font-retro text-sm text-poke-yellow uppercase">{settings.tier === "all" ? "Tous" : settings.tier}</span>
              </SettingRow>
            </div>

            {/* Inclus */}
            <SettingRow label="Inclus">
              <div className="flex gap-2 flex-wrap">
                {settings.allowLegendaries && (
                  <Tag text="Legendaires" color="rgba(255,180,0,0.15)" textColor="#FFB400" />
                )}
                {settings.allowMythicals && (
                  <Tag text="Mythiques" color="rgba(200,100,255,0.15)" textColor="#C864FF" />
                )}
                {settings.typeFilter && (
                  <Tag text={`Type: ${translateType(settings.typeFilter)}`} color="rgba(100,200,255,0.15)" textColor="#64C8FF" />
                )}
                {!settings.allowLegendaries && !settings.allowMythicals && !settings.typeFilter && (
                  <span className="text-[8px] text-white/20 font-display">Standard</span>
                )}
              </div>
            </SettingRow>

            {/* Blind mode */}
            <SettingRow label="Mode">
              {settings.blindMode ? (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs">🙈</span>
                  <span className="font-retro text-[9px] text-purple-400">Aveugle</span>
                </div>
              ) : (
                <span className="font-retro text-[9px] text-white/30">Normal</span>
              )}
            </SettingRow>
          </div>

          {/* Full settings button */}
          <button
            onClick={() => setShowSettings(true)}
            className="w-full px-5 py-3 font-pokemon text-[9px] text-white/40 hover:text-white/80 hover:bg-white/5 transition-all flex items-center justify-center gap-2"
            style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
          >
            ⚙ Tous les reglages
          </button>
        </div>

        </div>{/* end flex-row */}
      </div>{/* end flex-col */}

      <SettingsModal isOpen={showSettings} onClose={() => setShowSettings(false)} />
    </div>
  );
}

/* --- Sub-components --- */

function MenuButton({ onClick, gradient, glowColor, icon, label, sublabel }: {
  onClick: () => void;
  gradient: string;
  glowColor: string;
  icon: string;
  label: string;
  sublabel: string;
}) {
  return (
    <button
      onClick={onClick}
      className="group relative font-pokemon py-5 px-7 rounded-2xl text-white transition-all duration-300 hover:scale-[1.04] active:scale-[0.96] overflow-hidden text-left flex items-center gap-5"
      style={{
        background: gradient,
        border: "3px solid rgba(255,255,255,0.1)",
        boxShadow: `0 6px 28px ${glowColor}, inset 0 1px 0 rgba(255,255,255,0.2), inset 0 -2px 0 rgba(0,0,0,0.25)`,
      }}
    >
      {/* Animated border glow on hover */}
      <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{ boxShadow: `inset 0 0 20px ${glowColor}, 0 0 30px ${glowColor}` }} />

      {/* Shine sweep */}
      <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />

      {/* Icon with bounce on hover */}
      <span className="text-3xl relative z-10 group-hover:scale-125 group-hover:-rotate-6 transition-all duration-300 drop-shadow-lg">{icon}</span>

      <div className="relative z-10 flex-1">
        <div className="text-base tracking-wide">{label}</div>
        <div className="text-[9px] text-white/50 font-display normal-case mt-1">{sublabel}</div>
      </div>

      {/* Arrow with slide on hover */}
      <span className="relative z-10 text-white/20 group-hover:text-white/70 group-hover:translate-x-1 transition-all duration-300 text-xl">▸</span>
    </button>
  );
}

function SettingRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="font-pokemon text-[7px] text-white/25 uppercase tracking-wider mb-1">{label}</div>
      {children}
    </div>
  );
}

function Tag({ text, color, textColor }: { text: string; color: string; textColor: string }) {
  return (
    <span className="font-retro text-[8px] px-1.5 py-0.5 rounded"
      style={{ background: color, color: textColor }}>
      {text}
    </span>
  );
}
